import {NavigationProps, WizardNavigation, WizardNavigationButton} from "../../../_shared/wizard/WizardNavigation";
import {OrgUnit} from "../../../../models/organization";
import {SearchedMember} from "../../../../models/membership.models";
import React, {useEffect, useState} from "react";
import {makeStyles} from "@material-ui/core/styles";
import {useTranslation} from "react-i18next";
import {FormStatus, WithErrorAndProcessingStatus} from "../../../../models/shared";
import {OrgUnitWithProcessingStatus} from "../../../machines/move-machines/move-machine-wizard-steps/Step2Validation";
import {ArrowForwardIos} from "@material-ui/icons";
import {moveMember} from "../../../../api/organization-unit-functions";
import * as _ from "lodash";
import {ValidationRow} from "../../../_shared/wizard/ValidationRow";
import {Grid, Typography} from "@material-ui/core";
import UserAvatar from "../../../user-avatar/UserAvatar";
import Button from "@material-ui/core/Button";
import {useNavigate} from "react-router-dom";
import {fetchUserCapabilities} from "../../../../api/user-capability-functions";
import {useOrganization} from "../../../organizations/organization-context";
import {useCapability} from "../../../../auth/CapabilityContext";
import Breadcrumbs from "@material-ui/core/Breadcrumbs";
import clsx from "clsx";
import {OrganizationUnitSelectorDialog} from "../../../_shared/organization-unit-selector/OrganizationUnitSelectorDialog";

const useStyles = makeStyles((theme) => {
    const disabledOpacity = 0.6;

    return {
        to: {
            marginTop: theme.spacing(2),
            borderColor: (theme.palette as any).dark.line,
            border: 'thin solid',
            '&.Mui-disabled': {
                color: (theme.palette as any).dark.background,
                opacity: disabledOpacity
            }
        },
        toButtonError: {
            border: `thin ${theme.palette.error.light} solid`,
            color: theme.palette.error.light
        },

        dataContainer: {
            padding: theme.spacing(2),
        },
    }
})

interface Props extends NavigationProps {
    organizationId: number,
    onChangeDestinationOrgUnit: (orgUnit: OrgUnit) => void,
    form: { destinationOrgUnit: OrgUnit, members: SearchedMember[] }
}

interface MemberWithErrorAndProcessingStatus extends SearchedMember, WithErrorAndProcessingStatus {
}

export const Step2Validation: React.FC<Props> = ({organizationId, onChangeDestinationOrgUnit, form, ...props}) => {
    const classes = useStyles();
    const {t} = useTranslation();
    const navigate = useNavigate();
    const [, setOrganization] = useOrganization();

    const [openSelectOrgUnit, setOpenSelectOrgUnit] = useState<boolean>(false);

    const [displayedForm, setDisplayedForm] = useState({
        destinationOrgUnit: {processingStatus: FormStatus.none} as OrgUnitWithProcessingStatus,
        members: [] as MemberWithErrorAndProcessingStatus []
    });


    useEffect(() => {
        setDisplayedForm({
            destinationOrgUnit: {
                ...form.destinationOrgUnit,
                processingStatus: FormStatus.none,
                errorMessage: ''
            } as OrgUnitWithProcessingStatus,
            members: form.members.map(m => ({...m, processingStatus: FormStatus.none, errorMessage: ''}))
        });
    }, [form]);

    const changeDestinationOrgUnit = () => setOpenSelectOrgUnit(true);

    const selectedOrgUnitChanged = async (orgUnit: OrgUnit) => {
        setDisplayedForm(p => ({
            ...p,
            destinationOrgUnit: {...orgUnit, processingStatus: FormStatus.none, errorMessage: ''}
        }));
        setOpenSelectOrgUnit(false);
    };


    const onSubmitChange = (changeTab: () => void) => {
        onChangeDestinationOrgUnit(displayedForm.destinationOrgUnit);
        changeTab();
    }

    const isCompleted = (status: any) => status === FormStatus.successful || status === FormStatus.failed;
    const allCompleted = displayedForm.members.every((m: any) => isCompleted(m.processingStatus)) && isCompleted(displayedForm.destinationOrgUnit.processingStatus);

    const getDisabledButtons = (): Set<WizardNavigationButton> => {
        if (displayedForm.destinationOrgUnit.processingStatus === FormStatus.successful && !allCompleted) {
            return new Set([WizardNavigationButton.Previous, WizardNavigationButton.Close, WizardNavigationButton.Next, WizardNavigationButton.Save]);
        }
        return new Set<WizardNavigationButton>()
    }

    const onSave = async () => {
        setDisplayedForm(p => ({
            ...p,
            destinationOrgUnit: {...p.destinationOrgUnit, processingStatus: FormStatus.processing}
        }));

        if (!displayedForm.destinationOrgUnit.id) {
            setDisplayedForm(p => ({
                ...p,
                destinationOrgUnit: {...p.destinationOrgUnit, processingStatus: FormStatus.failed}
            }))
            return;
        }

        setDisplayedForm(p => ({
            ...p,
            destinationOrgUnit: {...p.destinationOrgUnit, processingStatus: FormStatus.successful}
        }));

        const setIndexedProcessingDisplayedForm = (index: number) => (f: { destinationOrgUnit: OrgUnitWithProcessingStatus, members: MemberWithErrorAndProcessingStatus[] }) => {
            f.members[index].processingStatus = FormStatus.processing;
            return {...f, members: f.members};
        }

        const setIndexedSuccessfulDisplayedForm = (index: number) => (f: { destinationOrgUnit: OrgUnitWithProcessingStatus, members: MemberWithErrorAndProcessingStatus[] }) => {
            f.members[index].processingStatus = FormStatus.successful;
            return {...f, machines: f.members};
        }

        const setIndexedFailedDisplayedForm = (index: number, e: any) => (f: { destinationOrgUnit: OrgUnitWithProcessingStatus, members: MemberWithErrorAndProcessingStatus[] }) => {
            f.members[index].processingStatus = FormStatus.failed;

            const errorType = _.get(e, 'response.data.error.type') || 'UnknownError';
            const msg = `_Common.Api.Messages.${errorType}`;
            f.members[index].errorMessage = msg;

            return {...f, members: f.members};
        }

        const updateSuccessful = (index: number) => (success: any) => {
            if (success) setDisplayedForm(setIndexedSuccessfulDisplayedForm(index));
            else throw new Error();
        };

        const updateFailed = (index: number) => (e: any) => {
            setDisplayedForm(setIndexedFailedDisplayedForm(index, e));
        }

        for (const index in displayedForm.members) {
            setDisplayedForm(setIndexedProcessingDisplayedForm(parseInt(index)));

            await moveMember(organizationId, displayedForm.members[index].organizationUnitId, displayedForm.destinationOrgUnit.id, displayedForm.members[index].userId)
                .then(updateSuccessful(parseInt(index)))
                .catch(updateFailed(parseInt(index)))
        }
    }

    const memberRow = (member: MemberWithErrorAndProcessingStatus) => {

        const fetchOrganizationAndCapabilities = async () => {
            await setOrganization({id: member.organizationId, name: member.organizationName});
        }

        const goToUnit = async () => {
            await fetchOrganizationAndCapabilities();
            navigate(`/organization-units/${member.organizationUnitId}/detail?tab=0`);
        };

        const goToOrg = async () => {
            await fetchOrganizationAndCapabilities();
            navigate(`/organization-units`);
        }

        return <ValidationRow
            processingStatus={member.processingStatus} errorMessage={member.errorMessage}>
            <Grid container item xs={11} spacing={4}>
                <Grid item> <UserAvatar userId={member.userId}/></Grid>
                <Grid item xs={4}>
                    <Typography variant="body1">{member.userFirstName} {member.userLastName}</Typography>
                    <Typography variant="body2"
                                color={'textPrimary'}>{member.email}</Typography>
                </Grid>
                <Grid item>
                    <Button onClick={goToOrg}><Typography
                        variant="body1">{member.organizationName}</Typography></Button>
                </Grid>
                <Grid item>
                    <Button onClick={goToUnit}>
                        <Breadcrumbs>
                            <Typography variant="body1">
                                {`../${member.organizationUnitName}`}
                            </Typography>
                        </Breadcrumbs>
                    </Button>
                </Grid>
            </Grid>
        </ValidationRow>
    };


    return <Grid container spacing={3}>
        <ValidationRow isSingleItem={true}
                       processingStatus={displayedForm.destinationOrgUnit.processingStatus}
                       errorMessage={displayedForm.destinationOrgUnit.processingStatus === FormStatus.failed ? 'Members.MoveMembers.Tabs.Validation.Destination.Error' : ''}>
            <Button
                disabled={displayedForm.members.length > 0 && displayedForm.members[0].processingStatus !== FormStatus.none}
                className={clsx(classes.to, displayedForm.destinationOrgUnit.processingStatus === FormStatus.failed && classes.toButtonError)}
                onClick={() => changeDestinationOrgUnit()}>
                {displayedForm.destinationOrgUnit.name ? displayedForm.destinationOrgUnit.name : t('Members.MoveMembers.Tabs.Validation.Destination.Title')}
                <ArrowForwardIos/>
            </Button>
        </ValidationRow>
        <Grid container className={classes.dataContainer} spacing={4}>
            {displayedForm.members.map(m => memberRow(m))}
        </Grid>
        <WizardNavigation onChangeTab={props.onChangeTab}
                          onClose={props.onClose}
                          onSave={onSave}
                          index={props.index}
                          lastIndex={props.lastIndex}
                          allCompleted={allCompleted}
                          onSubmitChange={onSubmitChange}
                          disabledButtons={getDisabledButtons()}/>
        <OrganizationUnitSelectorDialog open={openSelectOrgUnit} onSelectOrgUnit={selectedOrgUnitChanged}
                                        onCancel={() => setOpenSelectOrgUnit(false)}
                                        initialSelectedOrgUnit={displayedForm.destinationOrgUnit ? {
                                            id: displayedForm.destinationOrgUnit.id,
                                            name: displayedForm.destinationOrgUnit.name
                                        } : undefined}
                                        organizationId={organizationId}/>
    </Grid>
}
